import { useState } from 'react';
import PhoneFilters from '../components/PhoneFilters';
import { 
  Plus, 
  Search, 
  Filter, 
  Calendar, 
  TrendingUp, 
  DollarSign, 
  Users, 
  Activity,
  Pencil,
  Trash2,
  Eye,
  Download,
  ChevronDown,
  CreditCard,
  Wallet,
  Smartphone,
  Building
} from 'lucide-react';
import Table from '../components/Table.tsx';
import Modal from '../components/Modal.tsx';
import { PermissionPage, PermissionGate } from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';
import { 
  useLoanRepayments, 
  useCreateRepayment,
  useUpdateRepayment,
  useDeleteRepayment,
  useRepaymentAnalytics,
  PAYMENT_METHOD_OPTIONS,
  getPaymentMethodBadge,
  formatCurrency
} from '../hooks/useLoanRepayments.ts';
import { useMemberLoans } from '../hooks/useMemberLoans.ts';
import { useMembers } from '../hooks/useMembers.ts';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';
import { useDebounce } from '../hooks/useDebounce.ts';
import Select from '../components/ui/Select.tsx';

const quickFilterOptions = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
];

function LoanRepaymentsPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRepayment, setSelectedRepayment] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [memberFilter, setMemberFilter] = useState(null);
  const [loanFilter, setLoanFilter] = useState(null);
  const [paymentMethodFilter, setPaymentMethodFilter] = useState(null);
  const [quickFilter, setQuickFilter] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showAnalytics, setShowAnalytics] = useState(true);
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebounce(searchQuery, 500);
  const isAdmin = useIsAdmin();
  const { can } = usePermissions();

  const filters = {
    search: debouncedSearch,
    member_id: memberFilter?.value,
    loan_id: loanFilter?.value,
    payment_method: paymentMethodFilter?.value,
    quick_filter: quickFilter?.value,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    page,
    per_page: 15,
  };

  const { data: repaymentsData, isLoading } = useLoanRepayments(filters);
  const { data: analyticsData, isLoading: analyticsLoading } = useRepaymentAnalytics({
    start_date: dateFrom || undefined,
    end_date: dateTo || undefined,
  });
  const { data: loansData } = useMemberLoans();
  const { data: membersData } = useMembers();

  const createRepayment = useCreateRepayment();
  const updateRepayment = useUpdateRepayment();
  const deleteRepayment = useDeleteRepayment();

  // Create options for Select components
  const memberOptions = membersData?.data?.map(member => ({
    value: member.id,
    label: member.name,
  })) || [];

  const loanOptions = loansData?.data?.map(loan => ({
    value: loan.id,
                label: `${loan.member?.name} - ${formatCurrency(loan.principal)}`,
  })) || [];

  const paymentMethodOptions = PAYMENT_METHOD_OPTIONS.map(option => ({
    value: option.value,
    label: option.label,
  }));

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'CASH': return <DollarSign size={16} className="text-green-600" />;
      case 'MPESA': return <Smartphone size={16} className="text-yellow-600" />;
      case 'BANK': return <Building size={16} className="text-purple-600" />;
      case 'WALLET': return <Wallet size={16} className="text-blue-600" />;
      default: return <CreditCard size={16} className="text-gray-600" />;
    }
  };

  const columns = [
    {
      label: 'Member',
      field: 'member',
      sortable: true,
      render: row => (
        <div className="flex items-center">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
            <span className="text-blue-600 font-semibold text-sm">
              {row.member?.name?.charAt(0) || 'M'}
            </span>
          </div>
          <div>
            <div className="font-medium">{row.member?.name || '-'}</div>
            <div className="text-sm text-gray-500">{row.member?.phone_number || ''}</div>
          </div>
        </div>
      )
    },
    {
      label: 'Loan',
      field: 'loan',
      sortable: true,
      render: row => (
        <div>
          <div className="font-medium">{row.loan?.member?.name || '-'}</div>
          <div className="text-sm text-gray-500">
            {row.loan?.loan_type?.name || 'N/A'} - {formatCurrency(row.loan?.principal || 0)}
          </div>
        </div>
      )
    },
    {
      label: 'Amount',
      field: 'amount',
      sortable: true,
      render: row => (
        <span className="font-semibold text-green-600">
          {formatCurrency(row.amount)}
        </span>
      )
    },
    {
      label: 'Payment Method',
      field: 'payment_method',
      sortable: true,
      render: row => (
        <div className="flex items-center">
          {getPaymentMethodIcon(row.payment_method)}
          <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getPaymentMethodBadge(row.payment_method)}`}>
            {row.payment_method}
          </span>
        </div>
      )
    },
    {
      label: 'Payment Date',
      field: 'payment_date',
      sortable: true,
      render: row => new Date(row.payment_date).toLocaleDateString()
    },
    {
      label: 'Description',
      field: 'description',
      sortable: false,
      render: row => (
        <span className="text-sm text-gray-600 truncate max-w-32 block" title={row.description}>
          {row.description || '-'}
        </span>
      )
    },
    {
      label: 'Actions',
      field: 'actions',
      render: (row) => (
        <div className="flex space-x-2">
          <button
            className="text-blue-600 hover:text-blue-800"
            onClick={() => { setSelectedRepayment(row); setIsModalOpen(true); }}
            title="View/Edit"
          >
            <Eye size={18} />
          </button>
          {can(PERMISSIONS.ADD_LOAN_REPAYMENT) && (
            <button
              className="text-green-600 hover:text-green-800"
              onClick={() => { setSelectedRepayment(row); setIsModalOpen(true); }}
              title="Edit"
            >
              <Pencil size={18} />
            </button>
          )}
          {can(PERMISSIONS.DELETE_LOAN_REPAYMENT) && (
            <button
              className="text-red-600 hover:text-red-800"
              onClick={() => handleDeleteRepayment(row.id)}
              title="Delete"
            >
              <Trash2 size={18} />
            </button>
          )}
        </div>
      )
    },
  ];

  const handleCreateOrUpdateRepayment = (repaymentData) => {
    if (selectedRepayment) {
      updateRepayment.mutate({ id: selectedRepayment.id, ...repaymentData }, {
        onSuccess: () => {
          setIsModalOpen(false);
          setSelectedRepayment(null);
        }
      });
    } else {
      createRepayment.mutate(repaymentData, {
        onSuccess: () => {
          setIsModalOpen(false);
        }
      });
    }
  };

  const handleDeleteRepayment = (id) => {
    if (window.confirm('Are you sure you want to delete this repayment? This action cannot be undone.')) {
      deleteRepayment.mutate(id);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setMemberFilter(null);
    setLoanFilter(null);
    setPaymentMethodFilter(null);
    setQuickFilter(null);
    setDateFrom('');
    setDateTo('');
  };

  return (
    <PermissionPage permission={PERMISSIONS.VIEW_LOAN_REPAYMENT}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Loan Repayments</h2>
            <p className="text-gray-600 mt-1">Track and manage all loan repayment activities</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <TrendingUp size={16} className="mr-2" />
              {showAnalytics ? 'Hide' : 'Show'} Analytics
            </button>
            <PermissionGate permission={PERMISSIONS.ADD_LOAN_REPAYMENT}>
              <button
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                onClick={() => { setSelectedRepayment(null); setIsModalOpen(true); }}
              >
                <Plus size={16} className="mr-2" />
                Add Repayment
              </button>
            </PermissionGate>
          </div>
        </div>

        {/* Analytics Dashboard */}
        {showAnalytics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 kpi-grid">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Total Repayments</p>
                  <h3 className="text-2xl font-bold">
                    {analyticsLoading ? '...' : formatCurrency(analyticsData?.data?.summary?.total_repayments || 0)}
                  </h3>
                </div>
                <DollarSign size={32} className="text-blue-200" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Total Count</p>
                  <h3 className="text-2xl font-bold">
                    {analyticsLoading ? '...' : (analyticsData?.data?.summary?.repayment_count || 0).toLocaleString()}
                  </h3>
                </div>
                <Activity size={32} className="text-green-200" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Average Amount</p>
                  <h3 className="text-2xl font-bold">
                    {analyticsLoading ? '...' : formatCurrency(analyticsData?.data?.summary?.average_repayment || 0)}
                  </h3>
                </div>
                <TrendingUp size={32} className="text-purple-200" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm">Top Members</p>
                  <h3 className="text-2xl font-bold">
                    {analyticsLoading ? '...' : (analyticsData?.data?.top_members?.length || 0)}
                  </h3>
                </div>
                <Users size={32} className="text-orange-200" />
              </div>
            </div>
          </div>
        )}

        {/* Recent Activity & Payment Methods (if analytics shown) */}
        {showAnalytics && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Payment Methods Breakdown */}
            <div className="bg-white rounded-xl shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Payment Methods</h3>
              <div className="space-y-3">
                {Object.entries(analyticsData?.data?.payment_methods || {}).map(([method, stats]) => (
                  <div key={method} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      {getPaymentMethodIcon(method)}
                      <span className="ml-2 font-medium">{method}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">{formatCurrency(stats.total)}</div>
                      <div className="text-sm text-gray-500">{stats.count} payments</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Members */}
            <div className="bg-white rounded-xl shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Top Paying Members</h3>
              <div className="space-y-3">
                {analyticsData?.data?.top_members?.slice(0, 5).map((memberStat, index) => (
                  <div key={memberStat.member_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-blue-600 font-semibold text-sm">
                          {memberStat.member?.name?.charAt(0) || index + 1}
                        </span>
                      </div>
                      <span className="font-medium">{memberStat.member?.name || 'Unknown'}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">{formatCurrency(memberStat.total_paid)}</div>
                      <div className="text-sm text-gray-500">{memberStat.payment_count} payments</div>
                    </div>
                  </div>
                )) || []}
              </div>
            </div>
          </div>
        )}

        {/* Filters Section */}
        <PhoneFilters count={[memberFilter, loanFilter, paymentMethodFilter, quickFilter, searchQuery].filter(Boolean).length}>
<div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Filter size={20} className="mr-2 text-gray-600" />
              <h3 className="text-lg font-semibold">Filters</h3>
            </div>
            <button
              onClick={clearFilters}
              className="text-sm text-blue-800 hover:text-blue-700 font-medium"
            >
              Clear All
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search repayments..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <Search size={18} className="absolute left-3 top-2.5 text-gray-400" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Member</label>
              <Select
                options={memberOptions}
                value={memberFilter}
                onChange={setMemberFilter}
                isClearable
                placeholder="Filter by member..."
                classNamePrefix="select"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Loan</label>
              <Select
                options={loanOptions}
                value={loanFilter}
                onChange={setLoanFilter}
                isClearable
                placeholder="Filter by loan..."
                classNamePrefix="select"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
              <Select
                options={paymentMethodOptions}
                value={paymentMethodFilter}
                onChange={setPaymentMethodFilter}
                isClearable
                placeholder="Filter by method..."
                classNamePrefix="select"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quick Filter</label>
              <Select
                options={quickFilterOptions}
                value={quickFilter}
                onChange={setQuickFilter}
                isClearable
                placeholder="Quick time filters..."
                classNamePrefix="select"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
        </PhoneFilters>

        {/* Repayments Table */}
        <div className="bg-white rounded-xl shadow">
          <Table
            columns={columns}
            data={repaymentsData?.data || []}
            isLoading={isLoading}
            pagination={{
              currentPage: repaymentsData?.meta?.current_page || 1,
              totalPages: repaymentsData?.meta?.last_page || 1,
              onPageChange: setPage,
            }}
          />
        </div>

        {/* Add/Edit Repayment Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => { setIsModalOpen(false); setSelectedRepayment(null); }}
          title={selectedRepayment ? 'Edit Repayment' : 'Add New Repayment'}
        >
          <RepaymentForm
            initialData={selectedRepayment}
            onSubmit={handleCreateOrUpdateRepayment}
            onCancel={() => { setIsModalOpen(false); setSelectedRepayment(null); }}
            loans={loansData?.data || []}
            isEdit={!!selectedRepayment}
          />
        </Modal>
      </div>
    </PermissionPage>
  );
}

function RepaymentForm({ initialData, onSubmit, onCancel, loans, isEdit }) {
  const [loanId, setLoanId] = useState(initialData?.loan_id || '');
  const [amount, setAmount] = useState(initialData?.amount || '');
  const [paymentDate, setPaymentDate] = useState(
    initialData?.payment_date ? initialData.payment_date.split('T')[0] : new Date().toISOString().split('T')[0]
  );
  const [paymentMethod, setPaymentMethod] = useState(initialData?.payment_method || 'CASH');
  const [description, setDescription] = useState(initialData?.description || '');
  const [transactionReference, setTransactionReference] = useState(initialData?.transaction_reference || '');

  const selectedLoan = loans.find(loan => loan.id === parseInt(loanId));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      loan_id: parseInt(loanId),
      amount: parseFloat(amount),
      payment_date: paymentDate,
      payment_method: paymentMethod,
      description: description || undefined,
      transaction_reference: transactionReference || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">Loan</label>
        <select 
          className="w-full border rounded px-3 py-2" 
          value={loanId} 
          onChange={e => setLoanId(e.target.value)} 
          required
          disabled={isEdit}
        >
          <option value="">Select a loan</option>
          {loans.filter(loan => loan.status === 'APPROVED' || loan.status === 'DISBURSED').map(loan => (
            <option key={loan.id} value={loan.id}>
              {loan.member?.name} - {formatCurrency(loan.principal)} (Balance: {formatCurrency(loan.loan_balance)})
            </option>
          ))}
        </select>
      </div>

      {selectedLoan && (
        <div className="p-3 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-900">Loan Details</h4>
          <p className="text-sm text-blue-700">
            Principal: {formatCurrency(selectedLoan.principal)} | 
            Balance: {formatCurrency(selectedLoan.loan_balance)} | 
            Status: {selectedLoan.status}
          </p>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium mb-1">Amount</label>
        <input 
          type="number" 
          className="w-full border rounded px-3 py-2" 
          value={amount} 
          onChange={e => setAmount(e.target.value)} 
          required 
          min="0.01"
          step="0.01"
          max={selectedLoan?.loan_balance || undefined}
        />
        {selectedLoan && parseFloat(amount) > selectedLoan.loan_balance && (
          <p className="text-sm text-red-600 mt-1">
            Amount cannot exceed remaining balance of {formatCurrency(selectedLoan.loan_balance)}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Payment Date</label>
        <input 
          type="date" 
          className="w-full border rounded px-3 py-2" 
          value={paymentDate} 
          onChange={e => setPaymentDate(e.target.value)} 
          required 
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Payment Method</label>
        <select 
          className="w-full border rounded px-3 py-2" 
          value={paymentMethod} 
          onChange={e => setPaymentMethod(e.target.value)} 
          required
        >
          {PAYMENT_METHOD_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1" required>Transaction Reference</label>
        <input 
          type="text" 
          className="w-full border rounded px-3 py-2" 
          value={transactionReference} 
          onChange={e => setTransactionReference(e.target.value)} 
          placeholder="e.g., MPESA code, bank ref..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Description (Optional)</label>
        <textarea 
          className="w-full border rounded px-3 py-2" 
          value={description} 
          onChange={e => setDescription(e.target.value)} 
          rows="3"
          placeholder="Additional notes about this repayment..."
        />
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <button 
          type="button" 
          className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
          disabled={selectedLoan && parseFloat(amount) > selectedLoan.loan_balance}
        >
          {isEdit ? 'Update' : 'Add'} Repayment
        </button>
      </div>
    </form>
  );
} 


export default LoanRepaymentsPage;
